import {createContext, useContext, useState, ReactNode} from 'react'
import {Message, Student} from './types'
import {Processing} from '../../../components/Loaders'
import useData, {FetchMessagesResponse} from './hooks/useData'
import {FetchNextPageOptions, InfiniteQueryObserverResult} from 'react-query'

type ChatWindowState = 'no-chat' | 'new-chat' | 'view-chat'

interface ChatContextProps {
  data: {
    messages: Message[]
    classes: any[] // Replace with actual class type
    divisions: any[] // Replace with actual division type
    students: Student[] // Replace with actual student type
  }
  initialLoading: boolean
  chatWindowState: ChatWindowState
  startNewChat: () => void
  switchToNoChat: () => void
  selectedOldChat: Message | null
  selectOldChat: (chat: Message) => void
  messageDataHelpers: {
    fetchMoreMessages: (
      options?: FetchNextPageOptions
    ) => Promise<InfiniteQueryObserverResult<FetchMessagesResponse, unknown>>
    hasMoreMessages: boolean | undefined
    isFetchingMoreMessages: boolean
    isRefetchingMessages: boolean
  }
}

const ChatContext = createContext<ChatContextProps | undefined>(undefined)

export const ChatProvider = ({children}: {children: ReactNode}) => {
  const {classes, divisions, students, messages, initialLoading, messageDataHelpers} = useData()

  const [chatWindowState, setChatWindowState] = useState<ChatWindowState>('no-chat')
  const [selectedOldChat, setSelectedOldChat] = useState<Message | null>(null)

  const startNewChat = () => {
    setChatWindowState('new-chat')
    if (selectedOldChat !== null) {
      setSelectedOldChat(null)
    }
  }

  const switchToNoChat = () => {
    setChatWindowState('no-chat')
    if (selectedOldChat !== null) {
      setSelectedOldChat(null)
    }
  }

  const viewOldChat = () => {
    setChatWindowState('view-chat')
  }

  const selectOldChat = (chat: Message) => {
    setSelectedOldChat(chat)
    if (chatWindowState !== 'view-chat') {
      viewOldChat()
    }
  }

  if (initialLoading) {
    return <Processing />
  }

  return (
    <ChatContext.Provider
      value={{
        data: {
          messages: messages || [],
          classes: classes?.data || [],
          divisions: divisions?.data || [],
          students: students?.data || [],
        },
        initialLoading,
        chatWindowState,
        startNewChat,
        selectedOldChat,
        selectOldChat,
        messageDataHelpers,
        switchToNoChat,
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}

export const useChatContext = () => {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider')
  }
  return context
}
